import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TrendingUp } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Net Worth 2026: Income Sources Explained";
const DESC =
  "Raj Shamani has never disclosed a net worth figure. His five real income sources: Shamani Industries, Figuring Out, brand deals, speaking and 20+ investments.";
const URL = "https://rajshamani.life/raj-shamani-net-worth";
const OG = OG_ABOUT;

const faq = [
  {
    q: "What is Raj Shamani's net worth?",
    a: "Raj Shamani has never officially disclosed his net worth, and no audited figure exists in the public domain. The numbers circulating on aggregator websites are estimates built from guessed brand-deal rates and YouTube revenue, not verified financials. What can be stated accurately is the set of businesses and income streams behind it: Shamani Industries, Figuring Out Media, brand partnerships, international speaking fees and a portfolio of 20+ early-stage startup investments.",
  },
  {
    q: "How does Raj Shamani earn money?",
    a: "Through five main streams: the family manufacturing business Shamani Industries (polymer additives), Figuring Out Media and the Figuring Out podcast, brand partnerships across Instagram, YouTube and LinkedIn, paid international speaking engagements, and returns from angel investments in Indian startups.",
  },
  {
    q: "Is Raj Shamani a millionaire?",
    a: "There is no verified public filing that confirms any specific wealth bracket. He runs a multi-decade family manufacturing company, a media company and an angel portfolio, but any specific rupee or dollar figure attached to his name online is an unverified estimate.",
  },
  {
    q: "How much does Raj Shamani earn from the Figuring Out podcast?",
    a: "Podcast earnings are not disclosed. Revenue for a show at that scale typically comes from YouTube ad share, sponsor integrations, and the business opportunities the audience creates — not from any single line item that can be reliably estimated from outside.",
  },
  {
    q: "What is Shamani Industries' contribution to his income?",
    a: "Shamani Industries is the family's polymer additives manufacturing business, which predates all of Raj's media work. It remains a core business interest, though the company does not publish revenue figures.",
  },
  {
    q: "Why do net worth figures for Raj Shamani vary so much online?",
    a: "Because they are guesses. Most net-worth sites reverse-engineer a number from follower counts and assumed CPM rates, then copy each other. Without disclosed financials or public filings, those figures should be treated as speculation.",
  },
];

const streams = [
  ["Shamani Industries", "Family-owned polymer additives and speciality chemicals manufacturer serving Indian and export markets."],
  ["Figuring Out Media", "The media company behind the Figuring Out podcast — one of India's most-watched business interview shows."],
  ["Brand partnerships", "Sponsored content and long-term brand associations across Instagram, YouTube and LinkedIn."],
  ["Speaking engagements", "150+ stages across 25+ countries, including the United Nations and TEDx platforms."],
  ["Angel investments", "A portfolio of 20+ early-stage Indian startups across D2C, fintech, consumer and creator-economy categories."],
];

export const Route = createFileRoute("/raj-shamani-net-worth")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani net worth and income sources", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: NetWorthPage,
});

function NetWorthPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Net Worth" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <TrendingUp size={14} /> Money & business
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">net worth</span>, explained honestly
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani has never publicly disclosed his net worth, and no audited
            figure exists anywhere in the public domain. Every number you have seen
            on an aggregator site is an estimate built from guessed follower rates.
            What is verifiable is the set of businesses behind the question — and
            that is what this page documents.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Where the money actually comes from</h2>
        <p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
          Rather than a speculative figure, the useful answer is the structure of the
          income. Raj Shamani&apos;s earnings run across five distinct businesses and
          activities, each independent of the others.
        </p>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {streams.map(([k, v]) => (
            <div key={k} className="px-6 py-5">
              <dt className="font-bold mb-1">{k}</dt>
              <dd className="text-sm text-muted-foreground leading-relaxed">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Why online net-worth figures disagree</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed mb-4">
          Search &ldquo;Raj Shamani net worth&rdquo; and you will find figures that differ by
          an order of magnitude. That spread exists because none of those sites have
          access to financial statements. They start with a follower count, apply an
          assumed rate per sponsored post, add an invented YouTube CPM, multiply by
          twelve, and publish. The next site copies the first.
        </p>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          A manufacturing business like Shamani Industries cannot be estimated that way
          at all, and neither can an angel portfolio whose value only becomes real at an
          exit. Any credible answer would need disclosed financials, and those do not
          exist. Treating the estimates as facts is the single most common mistake made
          about this topic.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          For the full biography — age, family, education and career timeline — see the{" "}
          <Link to="/about" className="text-gold underline underline-offset-4">
            Raj Shamani wiki page
          </Link>
          , or read about{" "}
          <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
            Shamani Industries
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-net-worth" />

      <RelatedPages pages={["aion", "about", "ventures", "podcast", "book", "faq"]} />
    </div>
  );
}
